import React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  CheckSquare,
  ClipboardList,
  ExternalLink,
  FileText,
  Image,
  Shield,
  Target,
  XCircle,
} from 'lucide-react';
import type { LstTemplate } from '../../types';
import type { LinkedIndicatorAssessment } from '../../services/ielService';
import type { TemplateIndicatorLink } from '../../utils/lstIndicator';
import { templateHasLinkedIndicator } from '../../utils/lstIndicator';
import { buildSwiDrawerContent } from '../../utils/swiDrawerContent';

const sectionCls =
  'rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/30';
const sectionTitleCls =
  'mb-2.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400';
const labelCls = 'mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-400';
const inputCls =
  'w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-[12px] text-slate-700 outline-none focus:border-slate-400 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200';

function isImageUrl(url: string): boolean {
  return /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(url);
}

interface SwiExecutionSectionsProps {
  template: LstTemplate;
  indicatorMeta?: TemplateIndicatorLink | null;
  indicatorAssessment?: LinkedIndicatorAssessment | null;
  observacao: string;
  achados: string;
  onObservacaoChange: (value: string) => void;
  onAchadosChange: (value: string) => void;
}

export const SwiExecutionSections: React.FC<SwiExecutionSectionsProps> = ({
  template,
  indicatorMeta,
  indicatorAssessment,
  observacao,
  achados,
  onObservacaoChange,
  onAchadosChange,
}) => {
  const content = buildSwiDrawerContent(template, indicatorAssessment);
  const docUrl = template.swi_doc_url?.trim() || '';
  const showIndicator = indicatorMeta?.hasLink ?? templateHasLinkedIndicator(template);
  const indicatorName = indicatorAssessment?.name ?? indicatorMeta?.name ?? 'Indicador';
  const indicatorTarget = indicatorAssessment?.target ?? indicatorMeta?.target ?? null;
  const indicatorCurrent = indicatorAssessment?.current ?? null;
  const indicatorGap = indicatorAssessment?.gap ?? null;
  const indicatorUnit = indicatorAssessment?.unit ?? indicatorMeta?.unit ?? '';

  return (
    <div className="space-y-4">
      {/* Seção 1 — Por que esta rotina existe */}
      <section className={`${sectionCls} p-3`}>
        <h3 className={sectionTitleCls}>
          <Shield size={12} className="text-[#2F6FED]" />
          Por que esta rotina existe
        </h3>
        {content.objetivo ? (
          <div className="mb-3">
            <p className="text-[10px] font-semibold uppercase text-slate-400">Objetivo</p>
            <p className="mt-1 text-[13px] font-medium leading-relaxed text-slate-800 dark:text-slate-100">
              {content.objetivo}
            </p>
          </div>
        ) : (
          <p className="mb-3 text-[12px] italic text-slate-400">Objetivo não cadastrado.</p>
        )}

        {showIndicator && (
          <div className="rounded-md border-2 border-[#2F6FED]/30 bg-[#2F6FED]/8 p-3 dark:border-[#2F6FED]/40 dark:bg-[#2F6FED]/12">
            <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase text-slate-600 dark:text-slate-300">
              <Target size={11} className="text-[#2F6FED]" />
              Indicador protegido
            </p>
            <p className="text-[14px] font-bold text-slate-900 dark:text-slate-50">{indicatorName}</p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <IndicatorStatCard label="Meta" value={formatVal(indicatorTarget, indicatorUnit)} />
              <IndicatorStatCard label="Atual" value={formatVal(indicatorCurrent, indicatorUnit)} />
              <IndicatorStatCard
                label="Gap"
                value={formatGap(indicatorGap, indicatorUnit)}
                tone={
                  indicatorGap == null
                    ? 'neutral'
                    : indicatorGap >= 0
                      ? 'positive'
                      : 'negative'
                }
              />
            </div>
          </div>
        )}
      </section>

      {/* Seção 2 — Como verificar */}
      <section className={`${sectionCls} p-3`}>
        <h3 className={sectionTitleCls}>
          <CheckSquare size={12} className="text-emerald-600" />
          Como verificar
        </h3>
        <ul className="space-y-1">
          {content.passosVerificacao.map((passo, idx) => (
            <li
              key={idx}
              className="flex items-start gap-2.5 rounded-md border border-slate-100 bg-white px-2.5 py-2 dark:border-slate-800 dark:bg-slate-900/40"
            >
              <span
                className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border border-slate-300 text-[10px] text-slate-400 dark:border-slate-600"
                aria-hidden
              >
                □
              </span>
              <span className="text-[12px] leading-snug text-slate-700 dark:text-slate-200">{passo}</span>
            </li>
          ))}
        </ul>

        {docUrl && (
          <div className="mt-3 rounded-md border border-dashed border-slate-200 px-2.5 py-2 dark:border-slate-700">
            <a
              href={docUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-blue-600 hover:underline dark:text-blue-400"
            >
              {isImageUrl(docUrl) ? <Image size={12} /> : <ExternalLink size={12} />}
              {template.swi_mode === 'document' ? 'Abrir POP visual' : 'Ver documento de referência'}
            </a>
            {isImageUrl(docUrl) && (
              <img
                src={docUrl}
                alt="POP visual"
                className="mt-2 max-h-32 w-full rounded border border-slate-200 object-contain dark:border-slate-700"
              />
            )}
          </div>
        )}
      </section>

      {/* Seção 3 — Critérios de conformidade */}
      <section className={`${sectionCls} p-3`}>
        <h3 className={sectionTitleCls}>
          <ClipboardList size={12} className="text-slate-500" />
          Critérios de conformidade
        </h3>
        <div className="grid gap-2 sm:grid-cols-2">
          <CriteriaBox
            tone="conforme"
            title="Conforme (O) quando"
            items={content.conformeQuando}
          />
          <CriteriaBox
            tone="desvio"
            title="Desvio (Δ) quando"
            items={content.desvioQuando}
          />
        </div>
        {content.evidenciaEsperada && (
          <p className="mt-2.5 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
            <span className="font-semibold text-slate-600 dark:text-slate-300">Evidência esperada: </span>
            {content.evidenciaEsperada}
          </p>
        )}
      </section>

      {/* Seção 4 — Evidências */}
      <section className={`${sectionCls} p-3`}>
        <h3 className={sectionTitleCls}>
          <FileText size={12} className="text-violet-500" />
          Evidências
        </h3>
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Observações de campo</label>
            <textarea
              value={observacao}
              onChange={(e) => onObservacaoChange(e.target.value)}
              rows={2}
              className={`${inputCls} resize-none`}
              placeholder="Contexto do gemba, condições do turno, observações gerais…"
            />
          </div>
          <div>
            <label className={labelCls}>Achados da verificação</label>
            <textarea
              value={achados}
              onChange={(e) => onAchadosChange(e.target.value)}
              rows={2}
              className={`${inputCls} resize-none border-amber-200 focus:border-amber-400 dark:border-amber-900/50`}
              placeholder="O que foi observado fora do padrão? (usado ao registrar Δ)"
            />
          </div>
          <p className="text-[10px] leading-relaxed text-slate-400">
            Para registrar ações corretivas, selecione <strong className="text-amber-600">Δ Desvio</strong> no rodapé
            e use ação rápida ou escalonamento.
          </p>
        </div>
      </section>
    </div>
  );
};

function CriteriaBox({
  tone,
  title,
  items,
}: {
  tone: 'conforme' | 'desvio';
  title: string;
  items: string[];
}) {
  const isConforme = tone === 'conforme';
  return (
    <div
      className={`rounded-md border px-2.5 py-2 ${
        isConforme
          ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/40 dark:bg-emerald-950/20'
          : 'border-amber-200 bg-amber-50/50 dark:border-amber-900/40 dark:bg-amber-950/20'
      }`}
    >
      <p
        className={`mb-1.5 flex items-center gap-1 text-[10px] font-bold uppercase ${
          isConforme ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'
        }`}
      >
        {isConforme ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
        {title}
      </p>
      <ul className="space-y-1">
        {items.map((item, idx) => (
          <li
            key={idx}
            className="flex items-start gap-1.5 text-[11px] leading-snug text-slate-700 dark:text-slate-300"
          >
            <span className={`mt-1 h-1 w-1 shrink-0 rounded-full ${isConforme ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function IndicatorStatCard({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'positive' | 'negative';
}) {
  const valueCls =
    tone === 'positive'
      ? 'text-emerald-600'
      : tone === 'negative'
        ? 'text-red-600'
        : 'text-slate-800 dark:text-slate-100';

  return (
    <div className="rounded-md border border-white/70 bg-white/90 px-2 py-2 text-center dark:border-slate-800 dark:bg-slate-900/60">
      <p className="text-[8px] font-semibold uppercase text-slate-400">{label}</p>
      <p className={`mt-0.5 text-[13px] font-bold tabular-nums ${valueCls}`}>{value}</p>
    </div>
  );
}

function formatVal(value: number | null, unit: string): string {
  if (value == null) return '—';
  return `${value}${unit === '%' || !unit ? '%' : ` ${unit}`}`;
}

function formatGap(gap: number | null, unit: string): string {
  if (gap == null) return '—';
  const suffix = unit === '%' || !unit ? '%' : ` ${unit}`;
  return `${gap > 0 ? '+' : ''}${gap}${suffix}`;
}

export default SwiExecutionSections;
